import { Mail, Heart, Users, DollarSign, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useEffect } from 'react';
import { trackPageView } from '../utils/analytics';
import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';

const Scholarship = () => {
  useEffect(() => {
    trackPageView('Scholarship');
  }, []);

  return (
    <div className="min-h-screen bg-cream-50" data-testid="scholarship-page">
      <SiteHeader active="scholarship" />

      {/* Hero — dark, typography-led */}
      <section className="relative overflow-hidden bg-[#1a0d10] text-white py-20 md:py-28 px-4 md:px-6">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(124,29,51,0.22),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(139,30,63,0.16),transparent_60%)]" />
        <div className="absolute inset-0 grain" />

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <p className="eyebrow text-white/50 mb-5 animate-fade-in">Access for All</p>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-semibold leading-[0.95] tracking-tighter mb-8 animate-slide-up">
            <span className="block">Scholarship</span>
            <span className="block bg-gradient-to-br from-[#a8324a] via-[#c0455c] to-[#d9a15b] bg-clip-text text-transparent">
              Information
            </span>
          </h2>
          <p className="text-base md:text-lg text-white/70 font-light max-w-2xl mx-auto leading-relaxed animate-slide-up-1">
            Making the retreat accessible to every man who needs it.
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 md:py-28 px-4 md:px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-wine to-burgundy mb-6">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <p className="eyebrow text-gray-400 mb-3">Our Belief</p>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tighter text-gray-900 mb-5">
              Everyone deserves access
            </h3>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Financial barriers should never prevent someone from attending this transformative retreat.
              We offer scholarships so that every man who needs this experience can participate.
            </p>
          </div>

          {/* Info Cards — quiet, layout-led, matching landing's "Experience the journey" grid */}
          <div className="grid md:grid-cols-2 gap-px bg-gray-100 rounded-2xl overflow-hidden border border-gray-100 mb-16">
            <div className="bg-white p-8 md:p-10 hover:bg-gray-50/60 transition-colors group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-wine to-burgundy flex items-center justify-center mb-6 group-hover:scale-105 transition-transform">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-display text-2xl font-semibold mb-3 text-gray-900 tracking-tight">
                Financial assistance
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Partial and full scholarships are available based on individual circumstances. No one will
                be turned away due to financial constraints.
              </p>
            </div>

            <div className="bg-white p-8 md:p-10 hover:bg-gray-50/60 transition-colors group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-wine to-burgundy flex items-center justify-center mb-6 group-hover:scale-105 transition-transform">
                <Users className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-display text-2xl font-semibold mb-3 text-gray-900 tracking-tight">
                Confidential process
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                All scholarship applications are handled with complete confidentiality and respect. Your
                situation remains private.
              </p>
            </div>
          </div>

          {/* Contact Section */}
          <Card className="border border-gray-100 shadow-sm bg-gray-50/60">
            <CardContent className="p-10 md:p-14 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-wine to-burgundy mb-6">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <p className="eyebrow text-gray-400 mb-3">How to Apply</p>
              <h3 className="font-display text-3xl md:text-4xl font-semibold text-gray-900 tracking-tighter mb-4">
                Reach out — we're here to help
              </h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-8 max-w-xl mx-auto">
                If you need financial assistance to attend the ROJO Recovery Retreat, please email us with
                "Scholarship Request" in the subject line and share a bit about your situation. We'll reach
                out once all entries have been received.
              </p>
              <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-100 mb-8 max-w-md mx-auto">
                <p className="eyebrow text-gray-400 mb-2">Contact</p>
                <a
                  href="mailto:info@rojoretreat.com"
                  className="font-display text-2xl md:text-3xl font-semibold tracking-tight bg-gradient-to-br from-wine to-burgundy bg-clip-text text-transparent hover:opacity-80 transition-opacity break-all inline-block"
                >
                  info@rojoretreat.com
                </a>
              </div>
              <Button
                onClick={() =>
                  (window.location.href = 'mailto:info@rojoretreat.com?subject=Scholarship Request')
                }
                size="lg"
                className="bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600 text-white font-semibold text-sm px-7 py-5 rounded-full btn-lift group"
                data-testid="scholarship-email-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Us About Scholarships
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section — dark */}
      <section className="relative overflow-hidden py-20 md:py-28 px-4 md:px-6 bg-[#1a0d10] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(124,29,51,0.12),transparent_55%)]" />
        <div className="absolute inset-0 grain" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <p className="eyebrow text-white/40 mb-4">Next Step</p>
          <h3 className="font-display text-4xl md:text-5xl font-semibold tracking-tighter mb-4">
            Ready to register?
          </h3>
          <p className="text-white/60 mb-10 max-w-lg mx-auto text-sm md:text-base leading-relaxed">
            Head back to the main page to complete your registration and secure your spot.
          </p>
          <Link to="/">
            <Button
              size="lg"
              className="bg-white text-gray-900 hover:bg-gray-50 font-semibold text-base px-10 py-6 rounded-full btn-lift group"
            >
              Back to Registration
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Scholarship;
